import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import type { LotDto } from '@b-stock/listing-api-client'

import { getDealBadgeTrackingProps } from '@/buyer-portal/components/features/listing/helpers/getDealBadgeTrackingProps'
import type { P2SData } from '@/buyer-portal/hooks/useListingP2SData'
import type { FmvDealBadgeConfig } from '@/helpers/featureFlags'

import { getLotCategories } from './lot/categories'
import { getLotConditions } from './lot/conditions'

interface P2SAnalyticsData {
  average_seller_time_to_ship: string | null
  average_seller_time_to_ship_seconds: number | null
  is_average_seller_time_to_ship_shown: boolean
}

/**
 * Format P2S data for analytics tracking
 */
const formatP2SAnalyticsData = (p2sData: P2SData): P2SAnalyticsData => {
  const { avgPaidToShipMs, avgPaidToShipText } = p2sData

  // No data or invalid data - metric is not shown
  if (avgPaidToShipMs === null || avgPaidToShipMs < 0) {
    return {
      average_seller_time_to_ship: null,
      average_seller_time_to_ship_seconds: null,
      is_average_seller_time_to_ship_shown: false,
    }
  }

  return {
    average_seller_time_to_ship: avgPaidToShipText,
    average_seller_time_to_ship_seconds: Math.round(avgPaidToShipMs / 1000),
    is_average_seller_time_to_ship_shown: true,
  }
}

type ListingForAnalytics = Pick<
  OrchestratedListingDetails,
  '_id' | 'accountId' | 'seller'
> & {
  lot: Pick<LotDto, 'itemConditions' | 'categories' | 'overrides'>
}

const getListingAnalyticsData = (
  listingDetails: ListingForAnalytics | undefined,
  fmvBadgeFlag: FmvDealBadgeConfig,
  p2sData?: P2SData
) => {
  if (!listingDetails) return {}

  const {
    accountId,
    seller: {
      account: { displayName },
      storefront,
    },
    lot,
  } = listingDetails

  // Get deal badge tracking properties
  const dealBadgeTracking = getDealBadgeTrackingProps(
    listingDetails,
    fmvBadgeFlag
  )

  const conditions = getLotConditions(lot)
  const categories = getLotCategories(lot)

  // Format P2S analytics data if provided
  const p2sAnalytics = p2sData ? formatP2SAnalyticsData(p2sData) : {}

  const analyticsData = {
    listing_id: listingDetails._id,
    seller_account_id: accountId,
    seller_display_name: displayName,
    storefront_name: storefront?.name,
    storefront_id: storefront?._id,
    bstock_condition: conditions.join(',').toLowerCase() || undefined,
    bstock_category: categories.join(',').toLowerCase() || undefined,
    ...dealBadgeTracking,
    ...p2sAnalytics,
    // More properties can be added here based on requirements
  }

  return analyticsData
}

export default getListingAnalyticsData
